import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, Save, Sparkles } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const TRADES = ["av_low_voltage", "electrical", "plumbing", "hvac", "general", "labor"];

function Row({ item, onSaved }) {
  const [name, setName] = useState(item.name);
  const [price, setPrice] = useState(item.unit_price);
  const [cost, setCost] = useState(item.cost_price ?? "");
  const [unit, setUnit] = useState(item.unit);
  const [vendor, setVendor] = useState(item.vendor || "");
  const sell = parseFloat(price) || 0, c = parseFloat(cost) || 0;
  const marginPct = sell > 0 && c > 0 ? Math.round((sell - c) / sell * 100) : null;
  const save = async () => {
    try { await api.patch(`/pricebook/${item.id}`, { name, unit_price: sell, cost_price: cost === "" ? null : c, unit, vendor: vendor || null });
      toast.success("Saved"); onSaved?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/pricebook/${item.id}`); toast.success("Removed"); onSaved?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <tr className="border-b border-slate-800" data-testid={`pricebook-row-${item.id}`}>
      <td className="py-2 pr-2"><input value={name} onChange={(e) => setName(e.target.value)} data-testid={`pb-name-${item.id}`} className="w-full rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-sm text-white" /></td>
      <td className="px-2"><input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder=" - " data-testid={`pb-vendor-${item.id}`} className="w-28 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-sm text-white" /></td>
      <td className="px-2"><input value={unit} onChange={(e) => setUnit(e.target.value)} className="w-14 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-sm text-white" /></td>
      <td className="px-2"><input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="cost" data-testid={`pb-cost-${item.id}`} className="w-20 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-right text-sm text-white" /></td>
      <td className="px-2"><input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`pb-price-${item.id}`} className="w-24 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-right text-sm text-white" /></td>
      <td className="px-2 text-right"><span data-testid={`pb-margin-${item.id}`} className={`font-mono-tac text-xs ${marginPct === null ? "text-slate-600" : marginPct >= 30 ? "text-emerald-400" : "text-amber-400"}`}>{marginPct === null ? " - " : `${marginPct}%`}</span></td>
      <td className="flex items-center gap-2 py-2 pl-2">
        <button onClick={save} data-testid={`pb-save-${item.id}`} className="text-cyan-400 hover:text-cyan-300"><Save className="h-4 w-4" /></button>
        <button onClick={del} data-testid={`pb-del-${item.id}`} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export default function Pricebook() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ name: "", trade: "av_low_voltage", unit: "ea", unit_price: "", cost_price: "", vendor: "", category: "" });
  const load = () => api.get("/pricebook").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    try { await api.post("/pricebook", { ...f, unit_price: parseFloat(f.unit_price) || 0, cost_price: f.cost_price === "" ? null : parseFloat(f.cost_price), vendor: f.vendor || null });
      setF({ name: "", trade: f.trade, unit: "ea", unit_price: "", cost_price: "", vendor: "", category: "" }); load(); toast.success("Added"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const seed = async () => {
    try { const { data } = await api.post("/pricebook/seed-defaults"); toast.success(`Added ${data.added} starter items`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!items) return <div className="p-8"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="pricebook-page">
      <Eyebrow>Pricing</Eyebrow>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-3xl font-bold text-white"><BookOpen className="h-7 w-7 text-[#2E7BE4]" /> Company Pricebook</h1>
        <button onClick={seed} data-testid="seed-pricebook-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:border-[#2E7BE4]"><Sparkles className="h-4 w-4 text-[#2E7BE4]" /> Load starter AV pricebook</button>
      </div>
      <p className="mt-2 text-slate-400">Standard unit prices auto-fill new quotes so totals aren't zero.</p>

      <div className="mt-6 card-tac p-4">
        <div className="grid gap-2 sm:grid-cols-7">
          <input placeholder="Item name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pb-new-name"
            className="sm:col-span-2 rounded border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white" />
          <select value={f.trade} onChange={(e) => setF({ ...f, trade: e.target.value })} data-testid="pb-new-trade"
            className="rounded border border-slate-700 bg-[#0d1420] px-2 py-2 text-sm text-white">
            {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input placeholder="Vendor" value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} data-testid="pb-new-vendor"
            className="rounded border border-slate-700 bg-[#0d1420] px-2 py-2 text-sm text-white" />
          <input placeholder="$ cost" type="number" value={f.cost_price} onChange={(e) => setF({ ...f, cost_price: e.target.value })} data-testid="pb-new-cost"
            className="rounded border border-slate-700 bg-[#0d1420] px-2 py-2 text-sm text-white" />
          <input placeholder="$ sell" type="number" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="pb-new-price"
            className="rounded border border-slate-700 bg-[#0d1420] px-2 py-2 text-sm text-white" />
          <button onClick={add} data-testid="pb-add-btn" className="btn-amber inline-flex items-center justify-center gap-1 rounded py-2 text-sm"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      <div className="mt-6 card-tac overflow-x-auto p-4">
        {items.length === 0 ? (
          <p className="py-6 text-center text-slate-500">No pricebook items yet. Add one above or load the starter set.</p>
        ) : (
          <table className="w-full text-left">
            <thead><tr className="border-b border-slate-700 font-mono-tac text-[10px] uppercase text-slate-500">
              <th className="pb-2">Item</th><th className="px-2 pb-2">Vendor</th><th className="px-2 pb-2">Unit</th><th className="px-2 pb-2">Cost</th><th className="px-2 pb-2">Sell</th><th className="px-2 pb-2 text-right">Margin</th><th className="pb-2 pl-2"></th>
            </tr></thead>
            <tbody>{items.map((it) => <Row key={it.id} item={it} onSaved={load} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
